import React from 'react';
import { ColumnConfig, TableRow } from './types';
import {
  DocumentNameCell,
  TextCell,
  CurrencyCell,
  CurrencyDecimalCell,
  DateCell,
  BooleanCell,
} from './cellRenderers';
import { extractValue } from '../utils/dataFormatters';

interface GetColumnConfigsOptions {
  isLease: boolean;
  expandedRowIds: Set<string>;
  setExpandedRowIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

/**
 * Get column configurations based on document type
 */
export const getColumnConfigs = ({
  isLease,
  expandedRowIds,
  setExpandedRowIds,
}: GetColumnConfigsOptions): ColumnConfig[] => {
  const baseColumns: ColumnConfig[] = [
    {
      id: 'title',
      header: 'Document Name',
      accessor: 'title',
      defaultVisible: true,
      isSticky: true,
      minWidth: 250,
      className: 'document-name-column',
      documentTypes: ['lease', 'psa'],
      Cell: (props: any) => (
        <DocumentNameCell
          {...props}
          expandedRowIds={expandedRowIds}
          setExpandedRowIds={setExpandedRowIds}
        />
      ),
    },
    {
      id: 'tenantBuyer',
      header: isLease ? 'Tenant Name' : 'Buyer Name',
      accessor: 'tenantBuyer',
      defaultVisible: true,
      width: 280,
      minWidth: 250,
      className: 'tenant-buyer-column',
      documentTypes: ['lease', 'psa'],
      Cell: TextCell,
    },
    {
      id: 'landlordSeller',
      header: isLease ? 'Landlord Name' : 'Seller Name',
      accessor: 'landlordSeller',
      defaultVisible: true,
      width: 280,
      minWidth: 250,
      className: 'landlord-seller-column',
      documentTypes: ['lease', 'psa'],
      Cell: TextCell,
    },
    {
      id: 'propertyAddress',
      header: 'Property Address',
      accessor: 'propertyAddress',
      defaultVisible: true,
      width: 350,
      minWidth: 300,
      className: 'property-address-column',
      documentTypes: ['lease', 'psa'],
      Cell: TextCell,
    },
    {
      id: 'squareFeet',
      header: isLease ? 'Rentable SF' : 'Total SF',
      accessor: 'squareFeet',
      defaultVisible: true,
      width: 180,
      minWidth: 150,
      className: 'square-feet-column',
      documentTypes: ['lease', 'psa'],
      Cell: TextCell,
    },
  ];

  // Lease-specific columns
  const leaseColumns: ColumnConfig[] = isLease
    ? [
        {
          id: 'initialMonthlyRent',
          header: 'Initial Monthly Rent',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'initial_monthly_rent'),
          defaultVisible: true,
          width: 180,
          minWidth: 150,
          className: 'initial-monthly-rent-column',
          documentTypes: ['lease'],
          Cell: CurrencyCell,
        },
        {
          id: 'leaseTermMonths',
          header: 'Lease Term (Months)',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'lease_term_months'),
          defaultVisible: true,
          width: 150,
          minWidth: 130,
          className: 'lease-term-column',
          documentTypes: ['lease'],
          Cell: TextCell,
        },
        {
          id: 'rentPerSqftAnnual',
          header: 'Rent per SF (Annual)',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'rent_per_sqft_annual'),
          defaultVisible: false,
          width: 180,
          minWidth: 150,
          className: 'rent-per-sqft-column',
          documentTypes: ['lease'],
          Cell: CurrencyDecimalCell,
        },
        {
          id: 'securityDepositAmount',
          header: 'Security Deposit',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'security_deposit_amount'),
          defaultVisible: false,
          width: 180,
          minWidth: 150,
          className: 'security-deposit-column',
          documentTypes: ['lease'],
          Cell: CurrencyCell,
        },
        {
          id: 'camType',
          header: 'CAM Type',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'cam_type'),
          defaultVisible: false,
          width: 150,
          minWidth: 130,
          className: 'cam-type-column',
          documentTypes: ['lease'],
          Cell: TextCell,
        },
        {
          id: 'commencementDate',
          header: 'Commencement Date',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'commencement_date'),
          defaultVisible: false,
          width: 180,
          minWidth: 150,
          className: 'commencement-date-column',
          documentTypes: ['lease'],
          Cell: DateCell,
        },
        {
          id: 'terminationDate',
          header: 'Termination Date',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'termination_date'),
          defaultVisible: false,
          width: 180,
          minWidth: 150,
          className: 'termination-date-column',
          documentTypes: ['lease'],
          Cell: DateCell,
        },
        {
          id: 'renewalNoticeDeadline',
          header: 'Renewal Notice Deadline',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'renewal_notice_deadline'),
          defaultVisible: false,
          width: 200,
          minWidth: 180,
          className: 'renewal-notice-column',
          documentTypes: ['lease'],
          Cell: DateCell,
        },
      ]
    : [];

  // PSA-specific columns
  const psaColumns: ColumnConfig[] = !isLease
    ? [
        {
          id: 'totalPurchasePrice',
          header: 'Total Purchase Price',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'total_purchase_price'),
          defaultVisible: true,
          width: 200,
          minWidth: 180,
          className: 'total-purchase-price-column',
          documentTypes: ['psa'],
          Cell: CurrencyCell,
        },
        {
          id: 'pricePerSqft',
          header: 'Price per SF',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'price_per_sqft'),
          defaultVisible: true,
          width: 150,
          minWidth: 130,
          className: 'price-per-sqft-column',
          documentTypes: ['psa'],
          Cell: CurrencyDecimalCell,
        },
        {
          id: 'depositAmount',
          header: 'Deposit Amount',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'deposit_amount'),
          defaultVisible: false,
          width: 180,
          minWidth: 150,
          className: 'deposit-amount-column',
          documentTypes: ['psa'],
          Cell: CurrencyCell,
        },
        {
          id: 'propertyType',
          header: 'Property Type',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'property_type'),
          defaultVisible: false,
          width: 150,
          minWidth: 130,
          className: 'property-type-column',
          documentTypes: ['psa'],
          Cell: TextCell,
        },
        {
          id: 'soldAsIs',
          header: 'Sold As-Is',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'sold_as_is'),
          defaultVisible: false,
          width: 120,
          minWidth: 100,
          className: 'sold-as-is-column',
          documentTypes: ['psa'],
          Cell: BooleanCell,
        },
        {
          id: 'effectiveDate',
          header: 'Effective Date',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'effective_date'),
          defaultVisible: false,
          width: 180,
          minWidth: 150,
          className: 'effective-date-column',
          documentTypes: ['psa'],
          Cell: DateCell,
        },
        {
          id: 'closingDate',
          header: 'Closing Date',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'closing_date'),
          defaultVisible: false,
          width: 180,
          minWidth: 150,
          className: 'closing-date-column',
          documentTypes: ['psa'],
          Cell: DateCell,
        },
        {
          id: 'inspectionPeriodEnd',
          header: 'Inspection Period End',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'inspection_period_end'),
          defaultVisible: false,
          width: 200,
          minWidth: 180,
          className: 'inspection-period-column',
          documentTypes: ['psa'],
          Cell: DateCell,
        },
        {
          id: 'appraisalDeadline',
          header: 'Appraisal Deadline',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'appraisal_deadline'),
          defaultVisible: false,
          width: 180,
          minWidth: 150,
          className: 'appraisal-deadline-column',
          documentTypes: ['psa'],
          Cell: DateCell,
        },
        {
          id: 'financingContingencyDeadline',
          header: 'Financing Contingency Deadline',
          accessor: (row: TableRow) =>
            extractValue(
              row.originalDocument?.extractedDocumentTerm?.termsData,
              'financing_contingency_deadline'
            ),
          defaultVisible: false,
          width: 240,
          minWidth: 200,
          className: 'financing-deadline-column',
          documentTypes: ['psa'],
          Cell: DateCell,
        },
        {
          id: 'titleObjectionDeadline',
          header: 'Title Objection Deadline',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'title_objection_deadline'),
          defaultVisible: false,
          width: 200,
          minWidth: 180,
          className: 'title-objection-column',
          documentTypes: ['psa'],
          Cell: DateCell,
        },
        {
          id: 'earnestMoneyDepositDeadline',
          header: 'Earnest Money Deposit Deadline',
          accessor: (row: TableRow) =>
            extractValue(
              row.originalDocument?.extractedDocumentTerm?.termsData,
              'earnest_money_deposit_deadline'
            ),
          defaultVisible: false,
          width: 240,
          minWidth: 200,
          className: 'earnest-money-column',
          documentTypes: ['psa'],
          Cell: DateCell,
        },
        {
          id: 'finalWalkthroughDate',
          header: 'Final Walk-Through Date',
          accessor: (row: TableRow) =>
            extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, 'final_walkthrough_date'),
          defaultVisible: false,
          width: 200,
          minWidth: 180,
          className: 'walkthrough-date-column',
          documentTypes: ['psa'],
          Cell: DateCell,
        },
      ]
    : [];

  return [...baseColumns, ...leaseColumns, ...psaColumns];
};
